import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Card, Spinner, Form, ButtonGroup, Alert, ListGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { SketchPicker } from 'react-color';
import {
    createFloatingButtonInstance,
    createDefaultFloatingButtonItem,
    BUTTON_TYPE_NORMAL,
    BUTTON_TYPE_WHATSAPP,
} from '../../../Models/FloatingButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHandHoldingHeart,
    faCoffee,
    faSmile,
    faAppleAlt,
    faAnchor,
    faBicycle,
    faCheck,
    faGift,
    faGlobe,
    faPlus,
    faTrash,
    faBan,
    faArrowUp,
    faArrowDown,
} from '@fortawesome/free-solid-svg-icons';
import { LanguageService } from '../../../Services/Axios/Api';
import LanguageSelector from '../../../Components/LanguageSelector/LanguageSelector';

const NO_ICON = null;

const getButtonColors = (button) => ({
    buttonBg: button?.style?.FloatingDonateButton?.backgroundColor ?? '#ddd',
    buttonColor: button?.style?.FloatingDonateButton?.color ?? 'black',
    hoverBg: button?.style?.FloatingDonateButtonHover?.backgroundColor ?? '#ddd',
    hoverColor: button?.style?.FloatingDonateButtonHover?.color ?? 'black',
});

const cloneInstance = (instance) => {
    const newInstance = createFloatingButtonInstance();
    newInstance.setData(JSON.parse(JSON.stringify(instance.getData())));
    return newInstance;
};

const FloatingButtonBuilder = ({ id }) => {
    const [floatingButtonInstance, setFloatingButtonInstance] = useState(createFloatingButtonInstance());
    const [loading, setLoading] = useState(false);
    const [activeButtonIndex, setActiveButtonIndex] = useState(0);
    const [activeColorPicker, setActiveColorPicker] = useState(null);
    const [colors, setColors] = useState(getButtonColors(createDefaultFloatingButtonItem()));
    const colorSwatchRefs = useRef({});

    const [currentLanguage, setCurrentLanguage] = useState('');
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [languageAlertType, setLanguageAlertType] = useState('info');
    const [showLanguageAlert, setShowLanguageAlert] = useState(false);
    const [isLanguagesLoaded, setIsLanguagesLoaded] = useState(false);

    const buttons = floatingButtonInstance.getButtons();
    const activeButton = buttons[activeButtonIndex] || buttons[0];

    useEffect(() => {
        if (id) {
            loadFloatingButtonData();
        } else {
            loadLanguages();
        }
    }, [id]);

    useEffect(() => {
        if (!id && isLanguagesLoaded && currentLanguage) {
            loadFloatingButtonData();
        }
    }, [isLanguagesLoaded, currentLanguage, id]);

    useEffect(() => {
        const button = floatingButtonInstance.getButton(activeButtonIndex);
        if (button) {
            setColors(getButtonColors(button));
        }
    }, [activeButtonIndex, floatingButtonInstance]);

    const loadLanguages = async () => {
        try {
            const response = await LanguageService.getLanguages();
            setAvailableLanguages(response.result);

            const defaultLanguage = response.result.find(lang => lang.isDefault)?.code || 'en';
            setCurrentLanguage(defaultLanguage);
            setIsLanguagesLoaded(true);
        } catch (error) {
            console.error('Error loading languages:', error);
            toast.error('Failed to load languages');
            setCurrentLanguage('en');
            setIsLanguagesLoaded(true);
        }
    };

    const loadFloatingButtonData = async () => {
        try {
            setLoading(true);
            const instance = createFloatingButtonInstance();

            if (id) {
                await instance.load(id);
            } else {
                await instance.loadByLanguage(currentLanguage);
            }

            setFloatingButtonInstance(instance);
            setActiveButtonIndex(0);
            setColors(getButtonColors(instance.getButton(0)));
        } catch (error) {
            console.error('Error loading floating button data:', error);
            toast.error('Failed to load floating button data');
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageChange = async (newLanguage) => {
        setCurrentLanguage(newLanguage);
        setShowLanguageAlert(true);
        setLanguageAlertType('info');
        toast.success(`Switched to ${availableLanguages.find(l => l.code === newLanguage)?.name || newLanguage.toUpperCase()}`);
        setTimeout(() => setShowLanguageAlert(false), 3000);
    };

    const updateActiveButton = (updater) => {
        const newInstance = cloneInstance(floatingButtonInstance);
        const currentButton = { ...newInstance.getButton(activeButtonIndex) };
        updater(currentButton);
        newInstance.setButton(activeButtonIndex, currentButton);
        setFloatingButtonInstance(newInstance);
    };

    const handleTextChange = (e) => {
        updateActiveButton((button) => {
            button.text = e.target.value;
        });
    };

    const handleLinkChange = (e) => {
        updateActiveButton((button) => {
            button.link = e.target.value;
        });
    };

    const handleButtonTypeChange = (e) => {
        const buttonType = e.target.value;
        updateActiveButton((button) => {
            button.buttonType = buttonType;
            if (buttonType === BUTTON_TYPE_WHATSAPP) {
                button.phoneNumber = button.phoneNumber ?? '';
                button.helpText = button.helpText ?? '';
            }
        });
    };

    const handlePhoneNumberChange = (e) => {
        updateActiveButton((button) => {
            button.phoneNumber = e.target.value;
        });
    };

    const handleHelpTextChange = (e) => {
        updateActiveButton((button) => {
            button.helpText = e.target.value;
        });
    };

    const handleIconChange = (iconName) => {
        updateActiveButton((button) => {
            button.icon = iconName;
        });
    };

    const handlePositionChange = (position) => {
        updateActiveButton((button) => {
            button.position = position;
            button.style = button.style || {};
            button.style.FloatingDonateButton = button.style.FloatingDonateButton || {};

            if (position === 'left') {
                button.style.FloatingDonateButton.left = button.style.FloatingDonateButton.left || '20px';
                delete button.style.FloatingDonateButton.right;
            } else {
                button.style.FloatingDonateButton.right = button.style.FloatingDonateButton.right || '20px';
                delete button.style.FloatingDonateButton.left;
            }
        });
    };

    const handleStyleChange = (styleKey, value) => {
        updateActiveButton((button) => {
            button.style = button.style || {};
            button.style.FloatingDonateButton = button.style.FloatingDonateButton || {};
            button.style.FloatingDonateButton[styleKey] = value;
        });
    };

    const handleHoverStyleChange = (styleKey, value) => {
        updateActiveButton((button) => {
            button.style = button.style || {};
            button.style.FloatingDonateButtonHover = button.style.FloatingDonateButtonHover || {};
            button.style.FloatingDonateButtonHover[styleKey] = value;
        });
    };

    const handleVisibilityToggle = () => {
        updateActiveButton((button) => {
            button.isVisible = !button.isVisible;
        });
    };

    const handleAddButton = () => {
        const newInstance = cloneInstance(floatingButtonInstance);
        const newIndex = newInstance.addButton();
        setFloatingButtonInstance(newInstance);
        setActiveButtonIndex(newIndex);
    };

    const handleRemoveButton = (index) => {
        if (buttons.length <= 1) {
            toast.warning('At least one floating button is required');
            return;
        }

        const newInstance = cloneInstance(floatingButtonInstance);
        newInstance.removeButton(index);
        setFloatingButtonInstance(newInstance);
        setActiveButtonIndex((prev) => {
            if (prev === index) return Math.min(index, newInstance.getButtons().length - 1);
            if (prev > index) return prev - 1;
            return prev;
        });
    };

    const handleMoveButton = (index, direction) => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= buttons.length) return;

        const newInstance = cloneInstance(floatingButtonInstance);
        newInstance.moveButton(index, targetIndex);
        setFloatingButtonInstance(newInstance);

        setActiveButtonIndex((prev) => {
            if (prev === index) return targetIndex;
            if (prev === targetIndex) return index;
            return prev;
        });
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            await floatingButtonInstance.save();
            toast.success('Floating Button saved successfully');
        } catch (error) {
            toast.error('Failed to save Floating Button');
            console.error('Error saving Floating Button:', error);
        } finally {
            setLoading(false);
        }
    };

    const renderColorPicker = (show, colorKey, onChange, onClose, swatchId) => {
        if (!show) return null;
        const swatchElement = colorSwatchRefs.current[swatchId];
        if (!swatchElement) return null;

        const swatchRect = swatchElement.getBoundingClientRect();
        const pickerStyle = {
            position: 'fixed',
            left: `${swatchRect.left + swatchRect.width + 10}px`,
            top: `${swatchRect.top}px`,
            zIndex: 1000
        };

        return (
            <>
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999
                    }}
                    onClick={onClose}
                />
                <div style={pickerStyle}>
                    <SketchPicker
                        color={colors[colorKey]}
                        onChange={(color) => {
                            setColors(prevColors => ({ ...prevColors, [colorKey]: color.hex }));
                            onChange(color);
                        }}
                    />
                </div>
            </>
        );
    };

    const renderColorSwatch = (colorKey, onClick, swatchId) => (
        <div className="d-flex align-items-center">
            <div
                ref={el => colorSwatchRefs.current[swatchId] = el}
                style={{
                    width: '30px',
                    height: '30px',
                    backgroundColor: colors[colorKey],
                    border: '1px solid #ccc',
                    marginRight: '10px',
                    cursor: 'pointer',
                    borderRadius: '4px'
                }}
                onClick={onClick}
            />
            <span className="ms-2">{colors[colorKey]}</span>
        </div>
    );

    const iconList = [
        { name: NO_ICON, icon: faBan, label: 'No Icon' },
        { name: 'hand-holding-heart', icon: faHandHoldingHeart },
        { name: 'coffee', icon: faCoffee },
        { name: 'smile', icon: faSmile },
        { name: 'apple-alt', icon: faAppleAlt },
        { name: 'anchor', icon: faAnchor },
        { name: 'bicycle', icon: faBicycle },
        { name: 'gift', icon: faGift },
    ];

    const selectedIcon = activeButton?.icon ?? NO_ICON;
    const isIconSelected = (iconName) => {
        if (iconName === NO_ICON) {
            return selectedIcon == null || String(selectedIcon).trim() === '';
        }
        return selectedIcon === iconName;
    };

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <h2 className="mb-4">Floating Button Builder</h2>

            {!id && (
                <Card className="mb-4 language-management-card">
                    <Card.Header className="language-management-header">
                        <Row>
                            <Col md={6}>
                                <div className="d-flex align-items-center gap-3">
                                    <FontAwesomeIcon icon={faGlobe} className="text-primary" />
                                    <div>
                                        <h6 className="mb-0">Language Management</h6>
                                        <small className="text-muted">
                                            Managing content for: <strong>{availableLanguages.find(l => l.code === currentLanguage)?.name}</strong>
                                        </small>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6} className="d-flex justify-content-end">
                                <LanguageSelector
                                    currentLanguage={currentLanguage}
                                    availableLanguages={availableLanguages}
                                    onLanguageChange={handleLanguageChange}
                                    showBadge={false}
                                    showCopyFromLanguage={false}
                                />
                            </Col>
                        </Row>
                    </Card.Header>

                    {showLanguageAlert && (
                        <Card.Body className="p-3 language-alert-container">
                            <Alert
                                variant={languageAlertType}
                                dismissible
                                onClose={() => setShowLanguageAlert(false)}
                                className="mb-0"
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faGlobe} />
                                    <div>
                                        {languageAlertType === 'warning' ? (
                                            <>
                                                <strong>Translation Required:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version.
                                                {currentLanguage !== 'en' && ' Consider copying content from English to start with.'}
                                            </>
                                        ) : (
                                            <>
                                                <strong>Language Switched:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version of this page.
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Alert>
                        </Card.Body>
                    )}
                </Card>
            )}

            <Row className="mb-4">
                <Col md={3}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <span>Buttons</span>
                            <Button variant="outline-primary" size="sm" onClick={handleAddButton}>
                                <FontAwesomeIcon icon={faPlus} className="me-1" />
                                Add
                            </Button>
                        </Card.Header>
                        <ListGroup variant="flush">
                            {buttons.map((button, index) => (
                                <ListGroup.Item
                                    key={index}
                                    action
                                    active={index === activeButtonIndex}
                                    onClick={() => setActiveButtonIndex(index)}
                                    className="d-flex justify-content-between align-items-center gap-2"
                                >
                                    <div className="d-flex align-items-center flex-grow-1 min-width-0">
                                        {buttons.length > 1 && (
                                            <ButtonGroup size="sm" className="me-2 flex-shrink-0">
                                                <Button
                                                    variant="outline-secondary"
                                                    disabled={index === 0}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleMoveButton(index, 'up');
                                                    }}
                                                    title="Move up"
                                                >
                                                    <FontAwesomeIcon icon={faArrowUp} />
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    disabled={index === buttons.length - 1}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleMoveButton(index, 'down');
                                                    }}
                                                    title="Move down"
                                                >
                                                    <FontAwesomeIcon icon={faArrowDown} />
                                                </Button>
                                            </ButtonGroup>
                                        )}
                                        <span className="text-truncate">
                                            {button?.text || `Button ${index + 1}`}
                                        </span>
                                    </div>
                                    {buttons.length > 1 && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="flex-shrink-0"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveButton(index);
                                            }}
                                            title="Remove button"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    )}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Card>
                </Col>

                <Col md={9}>
                    <Row>
                        <Col md={6}>
                            <Card className="mb-4">
                                <Card.Header>General Settings — Button {activeButtonIndex + 1}</Card.Header>
                                <Card.Body>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Button Text</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={activeButton?.text || ''}
                                            onChange={handleTextChange}
                                            placeholder="Enter button text"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Button Type</Form.Label>
                                        <Form.Select
                                            value={activeButton?.buttonType || BUTTON_TYPE_NORMAL}
                                            onChange={handleButtonTypeChange}
                                        >
                                            <option value={BUTTON_TYPE_NORMAL}>Normal</option>
                                            <option value={BUTTON_TYPE_WHATSAPP}>WhatsApp Help Button</option>
                                        </Form.Select>
                                    </Form.Group>
                                    {(activeButton?.buttonType || BUTTON_TYPE_NORMAL) === BUTTON_TYPE_NORMAL ? (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Button Link</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={activeButton?.link || ''}
                                                onChange={handleLinkChange}
                                                placeholder="Enter button link"
                                            />
                                        </Form.Group>
                                    ) : (
                                        <>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Phone Number</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={activeButton?.phoneNumber || ''}
                                                    onChange={handlePhoneNumberChange}
                                                    placeholder="e.g. 966501234567"
                                                />
                                                <Form.Text className="text-muted">
                                                    Country code + number.
                                                </Form.Text>
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Help Text</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={activeButton?.helpText || ''}
                                                    onChange={handleHelpTextChange}
                                                    placeholder="Message prefix before page URL"
                                                />
                                                <Form.Text className="text-muted">
                                                    text that will be sent before the page URL
                                                </Form.Text>
                                            </Form.Group>
                                        </>
                                    )}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Button Position</Form.Label>
                                        <div className="mt-2">
                                            <ButtonGroup>
                                                <Button
                                                    variant={activeButton?.position === 'left' ? 'primary' : 'outline-primary'}
                                                    onClick={() => handlePositionChange('left')}
                                                >
                                                    Left Side
                                                </Button>
                                                <Button
                                                    variant={activeButton?.position === 'right' ? 'primary' : 'outline-primary'}
                                                    onClick={() => handlePositionChange('right')}
                                                >
                                                    Right Side
                                                </Button>
                                            </ButtonGroup>
                                        </div>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Select Icon</Form.Label>
                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                            {iconList.map((icon, index) => (
                                                <Button
                                                    key={index}
                                                    variant={isIconSelected(icon.name) ? 'primary' : 'light'}
                                                    className="icon-btn p-3 d-flex justify-content-center align-items-center"
                                                    onClick={() => handleIconChange(icon.name)}
                                                    title={icon.label || icon.name}
                                                    style={{
                                                        position: 'relative',
                                                        width: '50px',
                                                        height: '50px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={icon.icon} size="lg" />
                                                    {isIconSelected(icon.name) && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            bottom: 2,
                                                            right: 2,
                                                            backgroundColor: '#0d6efd',
                                                            borderRadius: '50%',
                                                            width: '16px',
                                                            height: '16px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <FontAwesomeIcon icon={faCheck} size="xs" color="white" />
                                                        </div>
                                                    )}
                                                </Button>
                                            ))}
                                        </div>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="switch"
                                            id={`visibility-switch-${activeButtonIndex}`}
                                            label="Show Floating Button"
                                            checked={activeButton?.isVisible !== false}
                                            onChange={handleVisibilityToggle}
                                        />
                                    </Form.Group>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="mb-4">
                                <Card.Header>Style Settings — Button {activeButtonIndex + 1}</Card.Header>
                                <Card.Body>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Button Background Color</Form.Label>
                                        <div className="d-flex align-items-center mt-2">
                                            {renderColorSwatch('buttonBg', () => setActiveColorPicker({ show: true, swatchId: `buttonBg-${activeButtonIndex}` }), `buttonBg-${activeButtonIndex}`)}
                                            {renderColorPicker(activeColorPicker?.show && activeColorPicker?.swatchId === `buttonBg-${activeButtonIndex}`, 'buttonBg', (color) => handleStyleChange("backgroundColor", color.hex), () => setActiveColorPicker(null), `buttonBg-${activeButtonIndex}`)}
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Button Text Color</Form.Label>
                                        <div className="d-flex align-items-center mt-2">
                                            {renderColorSwatch('buttonColor', () => setActiveColorPicker({ show: true, swatchId: `buttonColor-${activeButtonIndex}` }), `buttonColor-${activeButtonIndex}`)}
                                            {renderColorPicker(activeColorPicker?.show && activeColorPicker?.swatchId === `buttonColor-${activeButtonIndex}`, 'buttonColor', (color) => handleStyleChange("color", color.hex), () => setActiveColorPicker(null), `buttonColor-${activeButtonIndex}`)}
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Hover Background Color</Form.Label>
                                        <div className="d-flex align-items-center mt-2">
                                            {renderColorSwatch('hoverBg', () => setActiveColorPicker({ show: true, swatchId: `hoverBg-${activeButtonIndex}` }), `hoverBg-${activeButtonIndex}`)}
                                            {renderColorPicker(activeColorPicker?.show && activeColorPicker?.swatchId === `hoverBg-${activeButtonIndex}`, 'hoverBg', (color) => handleHoverStyleChange("backgroundColor", color.hex), () => setActiveColorPicker(null), `hoverBg-${activeButtonIndex}`)}
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Hover Text Color</Form.Label>
                                        <div className="d-flex align-items-center mt-2">
                                            {renderColorSwatch('hoverColor', () => setActiveColorPicker({ show: true, swatchId: `hoverColor-${activeButtonIndex}` }), `hoverColor-${activeButtonIndex}`)}
                                            {renderColorPicker(activeColorPicker?.show && activeColorPicker?.swatchId === `hoverColor-${activeButtonIndex}`, 'hoverColor', (color) => handleHoverStyleChange("color", color.hex), () => setActiveColorPicker(null), `hoverColor-${activeButtonIndex}`)}
                                        </div>
                                    </Form.Group>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <div className="mt-4 d-flex justify-content-end">
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={loading}
                    style={{ minWidth: '150px' }}
                >
                    {loading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Saving...
                        </>
                    ) : (
                        'Save Changes'
                    )}
                </Button>
            </div>
        </Container>
    );
};

export default FloatingButtonBuilder;
